import React, { useState, useEffect, useRef } from 'react';
import './HeroVideo.css';
import { AnimatedSection } from '../../AnimatedSection';
import { useNavigate } from 'react-router-dom';

const PHASES = {
  INIT: 0,
  TEXT_ANIMATION: 1,
  TEXT_FADE_OUT: 2,
  IMAGES_CENTER: 3,
  MOVE_TO_LOGO: 4,
  VIDEO_PLAY: 5
};

let hasAnimationPlayed = false; // persists across navigations

const HeroVideo = ({ onAnimationComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(PHASES.INIT);
  const [displayedFirstLine, setDisplayedFirstLine] = useState('');
  const [displayedSecondLine, setDisplayedSecondLine] = useState('');
  const [svgMounted, setSvgMounted] = useState(false);
  const videoRef = useRef(null);
  const animationIntervalsRef = useRef([]);
  const cancelTextAnimation = useRef(false);
  const firstLine = "THE TWO GREATEST";
  const secondLine = "WORDS IN SPORTS";
  const firstLineArray = firstLine.split('');
  const secondLineArray = secondLine.split('');
  const navigate = useNavigate();
  const playLogoAnimation = !hasAnimationPlayed;


const handleLogoClick = () => {
  console.log("SHOP NOW CLICKED");

  if (window.gtag) {
    window.gtag('event', 'shop_now_click', {
      location: 'hero_section',
      transport_type: 'beacon',
    });
  }

  setTimeout(() => {
    window.location.href =
      'https://www.amazon.com/stores/page/461F3B89-6764-45E0-9B2E-AB413358D21B';
  }, 300);
};


  // On mount, skip animation if already played
  useEffect(() => {
    if (hasAnimationPlayed) {
      setDisplayedFirstLine(firstLine);
      setDisplayedSecondLine(secondLine);
      setSvgMounted(true);
      setAnimationPhase(PHASES.VIDEO_PLAY);

      if (videoRef.current) videoRef.current.play().catch(() => {});
      if (onAnimationComplete) onAnimationComplete();
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  // Mark animation as played & mount SVGs
  useEffect(() => {
    if (animationPhase === PHASES.VIDEO_PLAY) {
      hasAnimationPlayed = true;
    }
    if (animationPhase >= PHASES.IMAGES_CENTER) {
      setSvgMounted(true);
    }
  }, [animationPhase]);

  // Skip button
  const handleSkipAnimation = () => {
    cancelTextAnimation.current = true;
    animationIntervalsRef.current.forEach(interval => clearInterval(interval));
    animationIntervalsRef.current = [];

    setDisplayedFirstLine(firstLine);
    setDisplayedSecondLine(secondLine);

    // Skip text animation but still play logo animation naturally
    setAnimationPhase(PHASES.TEXT_FADE_OUT);
  };

  // Main timeline
  useEffect(() => {
    if (hasAnimationPlayed) return; // skip timeline on navigation

    let timer;
    switch (animationPhase) {
      case PHASES.INIT:
        timer = setTimeout(() => setAnimationPhase(PHASES.TEXT_ANIMATION), 500);
        break;
      case PHASES.TEXT_ANIMATION:
        timer = setTimeout(() => setAnimationPhase(PHASES.TEXT_FADE_OUT), 6000);
        break;
      case PHASES.TEXT_FADE_OUT:
        timer = setTimeout(() => setAnimationPhase(PHASES.IMAGES_CENTER), 1000);
        break;
      case PHASES.IMAGES_CENTER:
        timer = setTimeout(() => setAnimationPhase(PHASES.MOVE_TO_LOGO), 1000);
        break;
      case PHASES.MOVE_TO_LOGO:
        timer = setTimeout(() => {
          setAnimationPhase(PHASES.VIDEO_PLAY);
          if (videoRef.current) videoRef.current.play().catch(() => {});
          if (onAnimationComplete) onAnimationComplete();
        }, 1000);
        break;
    }
    return () => clearTimeout(timer);
  }, [animationPhase, onAnimationComplete]);

  // Animate text
  useEffect(() => {
    if (animationPhase === PHASES.TEXT_ANIMATION) animateText();
  }, [animationPhase]);

  const animateText = async () => {
    cancelTextAnimation.current = false;
    setDisplayedFirstLine('');
    setDisplayedSecondLine('');
    animationIntervalsRef.current.forEach(interval => clearInterval(interval));
    animationIntervalsRef.current = [];

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    for (let i = 0; i < firstLineArray.length; i++) {
      if (cancelTextAnimation.current) return;
      setDisplayedFirstLine(prev => prev + firstLineArray[i]);
      await sleep(100);
    }

    await sleep(400);

    for (let i = 0; i < secondLineArray.length; i++) {
      if (cancelTextAnimation.current) return;
      setDisplayedSecondLine(prev => prev + secondLineArray[i]);
      await sleep(100);
    }

    await sleep(1000);
    if (!cancelTextAnimation.current) setAnimationPhase(PHASES.TEXT_FADE_OUT);
  };

  return (
    <section className="hero-video section">
      <div className="hero-container">
        {/* Animated text */}
        {(animationPhase === PHASES.TEXT_ANIMATION || animationPhase === PHASES.TEXT_FADE_OUT) && (
          <div className={`animated-text ${animationPhase === PHASES.TEXT_FADE_OUT ? 'fade-out' : ''}`}>
            <div className="first-line">{displayedFirstLine}</div>
            <div className="second-line">{displayedSecondLine}</div>
          </div>
        )}

        {/* Skip Animation Button */}
        {animationPhase === PHASES.TEXT_ANIMATION && (
          <button className="skip-animation-button" onClick={handleSkipAnimation}>
            SKIP
          </button>
        )}

        {/* Logos */}
{svgMounted && (
  <>
    { !hasAnimationPlayed ? (
      <>
        {/* Animated logos for first page load */}
        <svg className='game-mark-animate-logo'
              viewBox="0 0 160 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.0141 0.174411C5.76888 0.724663 2.43868 2.52779 0.985912 5.6044C0.13404 7.40865 -0.00163755 8.37347 1.47869e-05 12.6211C0.00129994 16.1829 0.0407724 16.6842 0.415853 17.8985C1.64207 21.868 4.58617 23.8898 10.3716 24.735C13.0601 25.1279 25.1159 25.0701 27.4744 24.6529C33.3011 23.6227 35.8995 21.5124 36.7179 17.146C36.8923 16.2157 36.9926 14.5572 36.9926 12.6046V9.5287H26.9867H16.9809V12.4305V15.3322L21.7084 15.3822L26.436 15.4323L26.4942 16.0509C26.5421 16.559 26.4266 16.7978 25.8475 17.3891C25.0544 18.1985 24.3731 18.4454 22.1215 18.7389C20.0183 19.0129 14.7785 18.8444 13.6125 18.465C12.2435 18.0197 11.387 17.3475 10.8987 16.3349C10.5225 15.5549 10.4547 15.1013 10.4002 12.9959C10.3227 9.99855 10.5357 8.90141 11.3936 7.87812C12.3026 6.79411 13.563 6.3595 16.5085 6.11417C20.6596 5.76876 24.3335 6.33813 25.3498 7.48437C25.6485 7.82153 25.9913 7.84196 31.3296 7.84196C34.4443 7.84196 36.9926 7.78254 36.9926 7.71001C36.9926 7.63748 36.6247 6.8063 36.1752 5.86285C34.4986 2.34468 31.6436 0.745651 26.0034 0.165975C23.8084 -0.0596735 13.1886 -0.0536737 11.0141 0.174411ZM49.5243 0.390127C49.4228 0.520193 49.013 1.21681 48.6135 1.93836C48.091 2.882 36.6485 23.3285 35.9487 24.5688C35.905 24.6462 38.3881 24.7094 41.4664 24.7094H47.0634L48.2085 22.552L49.3537 20.3949L57.1605 20.4436L64.9672 20.4925L66.0803 22.5951L67.1936 24.6977L73.0934 24.7035L78.993 24.7094L72.1582 12.4805L65.3232 0.251624L57.5159 0.202711C51.4087 0.164478 49.6686 0.205335 49.5243 0.390127ZM80.9956 0.281051C80.9272 0.350769 80.8713 5.87559 80.8713 12.5586V24.7094H86.0099H91.1485L91.1964 17.1239L91.2444 9.53825L93.7036 13.7973C95.0563 16.1396 97.0321 19.5532 98.0944 21.3827L100.026 24.7094H104.11H108.193L112.57 17.137C114.978 12.9721 117.009 9.60366 117.085 9.65145C117.161 9.69906 117.223 13.1066 117.223 17.2238V24.7094H122.272H127.32V12.4336V0.157916H119.097H110.873L108.311 4.98387C106.901 7.63823 105.542 10.1991 105.289 10.6749C104.243 12.6467 104.585 12.9483 101.721 7.52747C100.314 4.86579 98.8649 2.13984 98.5 1.46983L97.8361 0.251624L89.478 0.202894C84.881 0.176094 81.0639 0.211332 80.9956 0.281051ZM130.625 12.4336V24.7094H145.313H160V21.8044V18.8995H150.354H140.709L140.762 17.1659L140.815 15.4323L150.407 15.3837L160 15.3352V12.432V9.5287H150.354H140.709L140.762 7.7951L140.815 6.06151L150.407 6.01297L160 5.96442V3.06117V0.157916H145.313H130.625V12.4336ZM54.99 9.73017C53.8578 12.0056 52.9681 13.9048 53.0131 13.9506C53.0579 13.9963 54.9691 14.0111 57.2602 13.9834L61.4255 13.933L59.3689 9.76297C58.2376 7.46937 57.2528 5.59297 57.1805 5.59297C57.108 5.59297 56.1224 7.45476 54.99 9.73017Z"
                fill="white"
              />
            </svg>

            <svg
              className='culb7-mark-animate-logo'
              viewBox="0 0 148 74"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.000355731 0.118638C-0.0626563 0.220442 8.25363 8.60047 18.4812 18.7409L37.0766 37.1779L43.1608 37.068C46.507 37.0074 49.2995 37.0122 49.3662 37.0785C49.4329 37.1451 48.0978 38.5861 46.3992 40.2809L43.3109 43.3625L48.3964 48.4244L53.4821 53.4864L68.0234 38.9424L82.5646 24.3986L63.5493 24.3512L44.534 24.3039L38.4284 18.2218C35.0703 14.8766 32.3227 12.0765 32.3227 11.9994C32.3227 11.9221 51.1145 11.8589 74.0822 11.8589C97.0499 11.8589 115.842 11.9056 115.842 11.9626C115.842 12.0943 118.258 9.6722 89.0951 38.7741L63.9332 63.8834L67.8554 67.827C70.0125 69.996 72.2526 72.2723 72.8332 72.8854L73.8887 74L110.944 37.0776L148 0.155233L95.5212 0.146316C66.6578 0.141486 33.3837 0.091698 21.5788 0.0355945C7.41228 -0.0316553 0.0762312 -0.00341478 0.000355731 0.118638Z"
                fill="white"
              />
            </svg>
          </>
              ) : (
          <>
            <svg className='game-mark'
              viewBox="0 0 160 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.0141 0.174411C5.76888 0.724663 2.43868 2.52779 0.985912 5.6044C0.13404 7.40865 -0.00163755 8.37347 1.47869e-05 12.6211C0.00129994 16.1829 0.0407724 16.6842 0.415853 17.8985C1.64207 21.868 4.58617 23.8898 10.3716 24.735C13.0601 25.1279 25.1159 25.0701 27.4744 24.6529C33.3011 23.6227 35.8995 21.5124 36.7179 17.146C36.8923 16.2157 36.9926 14.5572 36.9926 12.6046V9.5287H26.9867H16.9809V12.4305V15.3322L21.7084 15.3822L26.436 15.4323L26.4942 16.0509C26.5421 16.559 26.4266 16.7978 25.8475 17.3891C25.0544 18.1985 24.3731 18.4454 22.1215 18.7389C20.0183 19.0129 14.7785 18.8444 13.6125 18.465C12.2435 18.0197 11.387 17.3475 10.8987 16.3349C10.5225 15.5549 10.4547 15.1013 10.4002 12.9959C10.3227 9.99855 10.5357 8.90141 11.3936 7.87812C12.3026 6.79411 13.563 6.3595 16.5085 6.11417C20.6596 5.76876 24.3335 6.33813 25.3498 7.48437C25.6485 7.82153 25.9913 7.84196 31.3296 7.84196C34.4443 7.84196 36.9926 7.78254 36.9926 7.71001C36.9926 7.63748 36.6247 6.8063 36.1752 5.86285C34.4986 2.34468 31.6436 0.745651 26.0034 0.165975C23.8084 -0.0596735 13.1886 -0.0536737 11.0141 0.174411ZM49.5243 0.390127C49.4228 0.520193 49.013 1.21681 48.6135 1.93836C48.091 2.882 36.6485 23.3285 35.9487 24.5688C35.905 24.6462 38.3881 24.7094 41.4664 24.7094H47.0634L48.2085 22.552L49.3537 20.3949L57.1605 20.4436L64.9672 20.4925L66.0803 22.5951L67.1936 24.6977L73.0934 24.7035L78.993 24.7094L72.1582 12.4805L65.3232 0.251624L57.5159 0.202711C51.4087 0.164478 49.6686 0.205335 49.5243 0.390127ZM80.9956 0.281051C80.9272 0.350769 80.8713 5.87559 80.8713 12.5586V24.7094H86.0099H91.1485L91.1964 17.1239L91.2444 9.53825L93.7036 13.7973C95.0563 16.1396 97.0321 19.5532 98.0944 21.3827L100.026 24.7094H104.11H108.193L112.57 17.137C114.978 12.9721 117.009 9.60366 117.085 9.65145C117.161 9.69906 117.223 13.1066 117.223 17.2238V24.7094H122.272H127.32V12.4336V0.157916H119.097H110.873L108.311 4.98387C106.901 7.63823 105.542 10.1991 105.289 10.6749C104.243 12.6467 104.585 12.9483 101.721 7.52747C100.314 4.86579 98.8649 2.13984 98.5 1.46983L97.8361 0.251624L89.478 0.202894C84.881 0.176094 81.0639 0.211332 80.9956 0.281051ZM130.625 12.4336V24.7094H145.313H160V21.8044V18.8995H150.354H140.709L140.762 17.1659L140.815 15.4323L150.407 15.3837L160 15.3352V12.432V9.5287H150.354H140.709L140.762 7.7951L140.815 6.06151L150.407 6.01297L160 5.96442V3.06117V0.157916H145.313H130.625V12.4336ZM54.99 9.73017C53.8578 12.0056 52.9681 13.9048 53.0131 13.9506C53.0579 13.9963 54.9691 14.0111 57.2602 13.9834L61.4255 13.933L59.3689 9.76297C58.2376 7.46937 57.2528 5.59297 57.1805 5.59297C57.108 5.59297 56.1224 7.45476 54.99 9.73017Z"
                fill="white"
              />
            </svg>

            <svg
              className='culb7-mark'
              viewBox="0 0 148 74"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.000355731 0.118638C-0.0626563 0.220442 8.25363 8.60047 18.4812 18.7409L37.0766 37.1779L43.1608 37.068C46.507 37.0074 49.2995 37.0122 49.3662 37.0785C49.4329 37.1451 48.0978 38.5861 46.3992 40.2809L43.3109 43.3625L48.3964 48.4244L53.4821 53.4864L68.0234 38.9424L82.5646 24.3986L63.5493 24.3512L44.534 24.3039L38.4284 18.2218C35.0703 14.8766 32.3227 12.0765 32.3227 11.9994C32.3227 11.9221 51.1145 11.8589 74.0822 11.8589C97.0499 11.8589 115.842 11.9056 115.842 11.9626C115.842 12.0943 118.258 9.6722 89.0951 38.7741L63.9332 63.8834L67.8554 67.827C70.0125 69.996 72.2526 72.2723 72.8332 72.8854L73.8887 74L110.944 37.0776L148 0.155233L95.5212 0.146316C66.6578 0.141486 33.3837 0.091698 21.5788 0.0355945C7.41228 -0.0316553 0.0762312 -0.00341478 0.000355731 0.118638Z"
                fill="white"
              />
            </svg>
          </>
        )}
        </>
        )}

        {/* Slash background */}
        <img
          src="/home_data/section1/slash_background.png"
          alt="Slash Background"
          className={`slash-background desktop-slash ${animationPhase >= PHASES.VIDEO_PLAY ? 'visible' : ''}`}
        />
        <div className={`mobile-section1-topback ${animationPhase >= PHASES.VIDEO_PLAY ? 'visible' : ''}`}></div>

        {/* Title & Button */}
        <div className={`title-more-button ${animationPhase >= PHASES.VIDEO_PLAY ? 'visible' : ''}`}>
          {animationPhase >= PHASES.VIDEO_PLAY && (
            <AnimatedSection>
              <div className='md:hidden block hero-title'>
                THE TWO GREATEST WORDS IN SPORTS.
              </div>
              <div className='md:block hidden'>
                <img
                  src="/home_data/section1/THE TWO GREATEST WORDS IN SPORTS.png"
                  alt="WORDS"
                />
              </div>
            </AnimatedSection>
          )}
          <a onClick={handleLogoClick} className={`site-dft-btn ${animationPhase >= PHASES.VIDEO_PLAY ? 'visible' : ''}`}>
            SHOP NOW
          </a>
        </div>

        {/* Video */}
        <div className={`video-wrapper ${animationPhase >= PHASES.VIDEO_PLAY ? 'visible' : ''}`}>
          <video
            ref={videoRef}
            className="background-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="/home_data/section1/1AM.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>
  );
};

export default HeroVideo;
